import { useContext, useState } from "react";
import { DumbbellIcon, HeartIcon, MailPlus, UserIcon } from "lucide-react";
import { Button, Label, Skeleton } from "@pushpress/pushpress-ui";
import { toast } from "react-toastify";
import { parseISO, fromUnixTime } from "date-fns";
import { phone } from "phone";
import { queryClient } from "../../../../../components/providers/ReactQueryProvider";
import {
  useUpdateUser,
  useUpdateClientUserProfile,
} from "../../../../../apis/userApi.hooks";
import { useSendEmail } from "../../../../../apis/messagesApi.hooks";
import { AppContext } from "./../../../../../context/app";
import ConfirmationModal from "../../shared/ConfirmationModal";
import { defaultToastOptions } from "../../../../../utils/misc";
import EditableInputField from "../../shared/EditableInputField";
import SectionHeader from "../../shared/SectionHeader";
import SectionTitle from "../../shared/SectionTitle";
import EditableGenderSelectField from "../../shared/EditableGenderSelectField";
import EditableDateField from "../../shared/EditableDateField";
import SubAccountsSection from "./SubAccountsSection";
import ParentAccountSection from "./ParentAccountSection";
import EditableAssignedToSelectField from "../../shared/EditableAssignedToSelectField";
import EditableReferredBySelectField from "../../shared/EditableReferredBySelectField";
import RemoveParentAccountModal from "./RemoveParentAccountModal";
import {
  EMAIL_REGEX,
  getUserType,
  convertDateStringOrTimestampToDate,
  getAssignedToStaffOptions,
  getProfilePayload,
  getParentAccountInfo,
  buildURLWithEnvironment,
  buildEmailParamsFromTemplate,
} from "../../../helpers";
import type { V2Member } from "../../../../../apis/userApi";
import type { UpdateProfileData, UsersInfoType } from "../../../types";

type PersonalInfoProps = {
  profileData: V2Member;
  isDataLoading: boolean;
  userUuid: string;
  usersInfo: UsersInfoType;
  isUsersInfoFetching: boolean;
  isProfileError: boolean;
};

export default function PersonalInfo({
  profileData,
  isDataLoading,
  userUuid,
  usersInfo,
  isUsersInfoFetching,
  isProfileError,
}: PersonalInfoProps) {
  const {
    client: {
      subdomain,
      company,
      email,
      addressLine1,
      phone: clientPhone,
      websiteUrl,
      id: clientId,
      uuid: clientUuid,
    },
  } = useContext(AppContext);
  const { mutate: updateProfile } = useUpdateClientUserProfile();
  const { mutate: updateUser, isLoading: isUpdatingUser } = useUpdateUser();
  const { mutate: sendEmail, isLoading: isSendingEmail } = useSendEmail();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [selectedSubAccountUserUuid, setSelectedSubAccountUserUuid] = useState<
    string | null
  >(null);

  const parentAccountInfo = getParentAccountInfo(profileData, usersInfo);
  const isSubAccount = profileData && profileData.parentUserId !== 0;

  const handleUpdateProfile = (data: UpdateProfileData[]) => {
    updateProfile(
      {
        clientUserUuid: profileData.uuid,
        data,
      },
      {
        onSuccess: () => {
          /* Update status in tanstack cache for instant visual update.
           The data will be re-fetched when the user re-visits the page */
          queryClient.setQueryData(
            ["client-user-profile", userUuid],
            (cachedData: V2Member) => {
              return {
                ...cachedData,
                ...getProfilePayload(data),
              };
            }
          );
        },
        onError: () => {
          toast.error(
            "There was an error updating the user",
            defaultToastOptions
          );
        },
      }
    );
  };

  const handleRemoveParentUser = () => {
    if (!selectedSubAccountUserUuid) {
      toast.error("No sub-account selected", defaultToastOptions);
      return;
    }
    updateUser(
      {
        userUuid: selectedSubAccountUserUuid,
        data: { parentUserId: 0 },
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          toast.success(
            "Parent account removed successfully",
            defaultToastOptions,
          );
          // Invalidate client-user-profile to refetch user profile data
          queryClient.invalidateQueries({ queryKey: ["client-user-profile"] });
        },
        onError: () => {
          toast.error("Failed to remove parent account", defaultToastOptions);
        },
      },
    );
  };

  const handleUpdateUserEmail = (email: string) => {
    updateUser(
      {
        userUuid,
        data: { email },
      },
      {
        onSuccess: () => {
          toast.success("Email updated successfully", defaultToastOptions);
          queryClient.setQueryData(
            ["client-user-profile", userUuid],
            (cachedData: V2Member) => {
              return {
                ...cachedData,
                email,
              };
            },
          );
        },
        onError: (error: Error) => {
          toast.error(
            `Failed to update email: ${error.message}`,
            defaultToastOptions,
          );
        },
      },
    );
  };

  const requestPersonalInfoEmail = () => {
    const body = buildEmailParamsFromTemplate({
      template: "email-profile-sent",
      company,
      clientId,
      clientUuid,
      userId: profileData?.userId,
      values: {
        client: {
          company,
          address: addressLine1,
          email,
          phone: clientPhone,
          url: websiteUrl,
        },
        cta_link: buildURLWithEnvironment(
          subdomain,
          `/open/accountsetup/${userUuid}`,
        ),
        cta_text: "Click Here",
      },
      userUuid,
      toAddress: profileData.email,
    });

    sendEmail(body, {
      onSuccess: () => {
        setIsRequestModalOpen(false);
        toast.success("Update profile request sent", defaultToastOptions);
      },
      onError: () => {
        toast.error(
          "There was an error sending profile update email",
          defaultToastOptions,
        );
      },
    });
  };

  const hasProfileData = profileData && !isDataLoading;
  const hasUsersInfoData = usersInfo && !isUsersInfoFetching;

  const isValidEmail = (data: string) => {
    return EMAIL_REGEX.test(data);
  };

  const isValidPhoneNumber = (data: string) => {
    if (!data.trim()) {
      return true;
    }
    return phone(data).isValid;
  };

  return (
    <div className="tw-space-y-4">
      <ConfirmationModal
        open={isRequestModalOpen}
        onOpenChange={() => setIsRequestModalOpen(false)}
        title="Request user personal information"
        content="Are you sure you want to email a request for personal information?"
        onClose={() => setIsRequestModalOpen(false)}
        onConfirm={requestPersonalInfoEmail}
        confirmationButtonText="Request"
        isLoading={isSendingEmail}
      />
      <SectionHeader
        title="Personal Information"
        actions={
          <div className="tw-flex tw-items-center tw-gap-4">
            <Button onClick={() => setIsRequestModalOpen(true)} size="sm">
              <MailPlus className="tw-mr-1 tw-h-4 tw-w-4" />
              Request Personal Information
            </Button>
          </div>
        }
      />
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-6">
        <div className="tw-gap-4 tw-mb-1">
          <SectionTitle icon={UserIcon} title="Basic Information" />
          <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
            <EditableInputField
              label="First Name"
              value={profileData && profileData.firstName}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "firstName",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
              validation={(value) => !!value.trim()}
            />
            <EditableInputField
              label="Last Name"
              value={profileData && profileData.lastName}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "lastName",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
              validation={(value) => !!value.trim()}
            />
            <EditableInputField
              label="Nickname"
              value={profileData && profileData.nickname}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "nickname",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableDateField
              label="Birthday"
              value={
                profileData && profileData.dob
                  ? parseISO(profileData.dob)
                  : undefined
              }
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "dob",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
              dateField="dob"
            />
            <EditableInputField
              label="Email"
              type="email"
              value={profileData && profileData.email}
              onSave={(value) => {
                handleUpdateUserEmail(value);
              }}
              hasData={hasProfileData}
              validation={(value) => isValidEmail(value)}
            />
            <EditableInputField
              label="Phone"
              type="phone"
              value={profileData && profileData.phone}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "phone",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
              validation={(value) => isValidPhoneNumber(value)}
            />
            <EditableGenderSelectField
              label="Gender"
              value={profileData && profileData.gender}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "gender",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="Street Address"
              value={profileData && profileData.address1}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "address1",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="Address Line Two"
              value={profileData && profileData.address2}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "address2",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="City"
              value={profileData && profileData.city}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "city",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="State"
              value={profileData && profileData.state}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "state",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="Postal Code"
              value={profileData && profileData.postalCode}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "postalCode",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="Country"
              value={profileData && profileData.country}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "country",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
          </div>
        </div>
        <div className="tw-gap-4 tw-mb-2">
          <SectionTitle icon={HeartIcon} title="Emergency Contact" />
          <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
            <EditableInputField
              label="Name"
              value={profileData && profileData.emergencyName}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "emergencyName",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="Relationship"
              value={profileData && profileData.emergencyRelationship}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "emergencyRelationship",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableInputField
              label="Phone"
              type="phone"
              value={profileData && profileData.emergencyPhone}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "emergencyPhone",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
              validation={(value) => isValidPhoneNumber(value)}
            />
          </div>
        </div>
        <div className="tw-gap-4 tw-mb-2">
          <SectionTitle icon={DumbbellIcon} title="Gym Details" />
          <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
            <EditableDateField
              label="Member Since"
              value={convertDateStringOrTimestampToDate(
                profileData && profileData.memberSince
              )}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "memberSince",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
              dateField="memberSince"
            />
            <EditableDateField
              label="First Check-in"
              value={
                profileData && profileData.firstCheckinTimestamp
                  ? fromUnixTime(profileData.firstCheckinTimestamp)
                  : undefined
              }
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "firstCheckinTimestamp",
                    value,
                  },
                ]);
              }}
              hasData={hasProfileData}
              dateField="firstCheckinTimestamp"
            />
            <div className="tw-space-y-1">
              <Label className="tw-text-xs tw-text-neutral-600">
                User Type
              </Label>
              {!hasProfileData ? (
                <Skeleton className="tw-h-4 tw-w-1/2" />
              ) : (
                <p className="tw-font-medium">
                  {getUserType(profileData && profileData.membershipRole)}
                </p>
              )}
            </div>
            <EditableInputField
              label="Custom ID"
              value={profileData && profileData.externalId?.toString()}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "externalId",
                    value: value ? Number(value) : null,
                  },
                ]);
              }}
              hasData={hasProfileData}
            />
            <EditableReferredBySelectField
              label="Referred By"
              referredById={profileData && profileData.referredById?.toString()}
              referredByOther={profileData && profileData.referredByOther}
              referredByUserId={
                profileData && profileData.referredByUserId?.toString()
              }
              onSave={(referredById, referredByOther, referredByUserId) =>
                handleUpdateProfile([
                  {
                    field: "referredById",
                    value: Number(referredById),
                  },
                  {
                    field: "referredByOther",
                    value: referredByOther,
                  },
                  {
                    field: "referredByUserId",
                    value: Number(referredByUserId),
                  },
                ])
              }
              hasData={hasUsersInfoData}
              members={usersInfo?.members || []}
              staff={usersInfo?.staff || []}
            />
            <EditableAssignedToSelectField
              label="Assigned To"
              value={profileData && profileData.assignedToUserId?.toString()}
              onSave={(value) => {
                handleUpdateProfile([
                  {
                    field: "assignedToUserId",
                    value: Number(value),
                  },
                ]);
              }}
              hasData={hasUsersInfoData}
              options={getAssignedToStaffOptions(usersInfo?.staff || [])}
            />
          </div>
        </div>
      </div>
      {!isSubAccount && (
        <SubAccountsSection
          userUuid={userUuid}
          profileData={profileData}
          isProfileError={isProfileError}
          hasData={hasProfileData}
          handleOpenModal={() => setIsModalOpen(true)}
          setSelectedSubAccountUserUuid={setSelectedSubAccountUserUuid}
        />
      )}
      {isSubAccount && (
        <ParentAccountSection
          parentAccountInfo={parentAccountInfo}
          subAccountUserUuid={userUuid}
          handleOpenModal={() => setIsModalOpen(true)}
          setSelectedSubAccountUserUuid={setSelectedSubAccountUserUuid}
        />
      )}
      <RemoveParentAccountModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        parentAccountName={
          isSubAccount
            ? `${parentAccountInfo?.firstName} ${parentAccountInfo?.lastName}`
            : `${profileData?.firstName} ${profileData?.lastName}`
        }
        subAccountUserUuid={selectedSubAccountUserUuid}
        handleRemoveParentUser={handleRemoveParentUser}
        isUpdating={isUpdatingUser}
      />
    </div>
  );
}
